import type { Point } from '../types';

/**
 * Shared polyline arithmetic for orthogonal edges.
 *
 * Routes arrive here as flat `[x0,y0,x1,y1,...]` arrays, which is what Konva
 * draws and what the geometry code passes around; the `Point[]` variants exist
 * for the layout side, which thinks in corners.
 */

/** Append `x,y` unless it repeats the point already at the end. */
function push(out: number[], x: number, y: number): void {
    const n = out.length;
    if (n >= 2 && Math.abs(out[n - 2] - x) < 0.01 && Math.abs(out[n - 1] - y) < 0.01) return;
    out.push(x, y);
}

/**
 * Collapse collinear runs and zero-length stubs. Konva happily draws a
 * degenerate corner, but the rounded-join renderer needs real segments and the
 * legibility metrics count every stub as a segment that can overlap.
 */
export function simplifyPolyline(pts: number[]): number[] {
    const out: number[] = [];
    for (let i = 0; i + 1 < pts.length; i += 2) push(out, pts[i], pts[i + 1]);
    if (out.length < 6) return out;
    const kept: number[] = [out[0], out[1]];
    for (let i = 2; i + 3 < out.length; i += 2) {
        const [px, py] = [kept[kept.length - 2], kept[kept.length - 1]];
        const [cx, cy] = [out[i], out[i + 1]];
        const [nx, ny] = [out[i + 2], out[i + 3]];
        const collinearV = Math.abs(px - cx) < 0.01 && Math.abs(cx - nx) < 0.01;
        const collinearH = Math.abs(py - cy) < 0.01 && Math.abs(cy - ny) < 0.01;
        if (collinearV || collinearH) continue;
        kept.push(cx, cy);
    }
    kept.push(out[out.length - 2], out[out.length - 1]);
    return kept;
}

/** `simplifyPolyline` for a list of corners. */
export function simplifyPoints(points: Point[]): Point[] {
    const flat: number[] = [];
    for (const point of points) flat.push(point.x, point.y);
    const simplified = simplifyPolyline(flat);
    const out: Point[] = [];
    for (let i = 0; i + 1 < simplified.length; i += 2) out.push({ x: simplified[i], y: simplified[i + 1] });
    return out;
}

/**
 * Replace each corner with a quarter arc, as an SVG-ish point stream Konva can
 * draw with `tension: 0`. Konva has no per-corner radius, so the rounding is
 * baked into the geometry: each corner becomes two points inset along the
 * incoming and outgoing legs plus a short chamfer between them.
 *
 * `radius` is clamped per corner to half the shorter adjacent leg so a tight
 * jog degrades to a sharp turn instead of overshooting into the next segment.
 */
export function roundCorners(pts: number[], radius: number, samples = 4): number[] {
    if (radius <= 0 || pts.length < 6) return pts;
    const out: number[] = [pts[0], pts[1]];
    for (let i = 2; i + 3 < pts.length; i += 2) {
        const px = pts[i - 2], py = pts[i - 1];
        const cxx = pts[i], cyy = pts[i + 1];
        const nx = pts[i + 2], ny = pts[i + 3];
        const inLen = Math.hypot(cxx - px, cyy - py);
        const outLen = Math.hypot(nx - cxx, ny - cyy);
        const r = Math.min(radius, inLen / 2, outLen / 2);
        if (r < 0.5) {
            out.push(cxx, cyy);
            continue;
        }
        const inUx = (cxx - px) / (inLen || 1), inUy = (cyy - py) / (inLen || 1);
        const outUx = (nx - cxx) / (outLen || 1), outUy = (ny - cyy) / (outLen || 1);
        const sx = cxx - inUx * r, sy = cyy - inUy * r;
        const ex = cxx + outUx * r, ey = cyy + outUy * r;
        out.push(sx, sy);
        // Quadratic Bézier with the corner as the control point traces the arc.
        for (let s = 1; s < samples; s++) {
            const t = s / samples;
            const mt = 1 - t;
            out.push(
                mt * mt * sx + 2 * mt * t * cxx + t * t * ex,
                mt * mt * sy + 2 * mt * t * cyy + t * t * ey,
            );
        }
        out.push(ex, ey);
    }
    out.push(pts[pts.length - 2], pts[pts.length - 1]);
    return out;
}
